/**
 * Deal Room Communication History
 * Real-time chat with WebSocket, typing indicator, notification sounds, and file/image uploads
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { MessageCircle, Send, Loader2, Video, Paperclip, Image as ImageIcon, X, FileText, Download, Trash2, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { messagesApi } from '@/services/api/messages.api';
import { ContactRequest } from '@/services/api/contact-requests.api';
import { wsService } from '@/services/websocket.service';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { playMessageSound } from '@/utils/notification-sounds';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';

interface Attachment {
  name: string;
  url: string;
  type: string; // mime type
  size: number;
}

interface Message {
  id: string;
  content: string;
  senderId: string;
  senderName?: string;
  createdAt: string;
  attachments?: Attachment[];
}

interface TypingUser {
  userId: string;
  name?: string;
  timeout?: ReturnType<typeof setTimeout>;
}

interface DealCommunicationHistoryProps {
  contactRequest: ContactRequest;
  language?: string;
}

function normalizeMessage(m: any): Message {
  return {
    id: m.id,
    content: m.content,
    senderId: m.senderId || m.sender_id || '',
    senderName: m.senderName || m.sender_name || '',
    createdAt: m.createdAt || m.created_at || new Date().toISOString(),
    attachments: m.attachments || [],
  };
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageType(type: string): boolean {
  return type.startsWith('image/');
}

export function DealCommunicationHistory({ contactRequest, language = 'en' }: DealCommunicationHistoryProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [imagePreviews, setImagePreviews] = useState<Record<number, string>>({});
  const [lightboxImage, setLightboxImage] = useState<{ url: string; name: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const isFr = language === 'fr';

  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const ALLOWED_FILE_TYPES = [
    ...ALLOWED_IMAGE_TYPES,
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv',
  ];

  // Load or create conversation
  useEffect(() => {
    if (contactRequest.status !== 'accepted') {
      setIsLoading(false);
      return;
    }

    const initConversation = async () => {
      try {
        const conversations = await messagesApi.getConversations();
        const existing = conversations.find(
          (c: any) => c.metadata?.dealId === contactRequest.dealId && c.metadata?.contactRequestId === contactRequest.id
        );

        if (existing) {
          setConversationId(existing.id);
          const msgs = await messagesApi.getMessages(existing.id);
          setMessages(msgs.map(normalizeMessage));
        } else {
          const participantIds = [contactRequest.buyerId, contactRequest.sellerId].filter(Boolean) as string[];
          const conv = await messagesApi.createConversation({
            participantIds,
            title: `Deal: ${contactRequest.dealName || contactRequest.dealId}`,
          });
          setConversationId(conv.id);
        }
      } catch (error) {
        console.error('Failed to init conversation:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initConversation();
  }, [contactRequest]);

  // WebSocket: join room, listen for messages and typing
  useEffect(() => {
    if (!conversationId) return;

    wsService.connect();
    wsService.emit('join_conversation', { conversationId });

    const unsubMsg = wsService.on('new_message', (msg: any) => {
      if (msg.conversationId === conversationId) {
        const senderId = msg.senderId || msg.sender_id || '';
        const isFromMe = senderId === user?.id || senderId === user?.email;

        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, normalizeMessage(msg)];
        });

        if (!isFromMe) {
          playMessageSound();
        }

        setTypingUsers((prev) => prev.filter((t) => t.userId !== senderId));
      }
    });

    const unsubTyping = wsService.on('user_typing', (data: { userId: string; conversationId: string; isTyping: boolean }) => {
      if (data.conversationId !== conversationId) return;
      if (data.userId === user?.id || data.userId === user?.email) return;

      setTypingUsers((prev) => {
        const existing = prev.find((t) => t.userId === data.userId);
        if (existing?.timeout) clearTimeout(existing.timeout);

        if (data.isTyping) {
          const timeout = setTimeout(() => {
            setTypingUsers((p) => p.filter((t) => t.userId !== data.userId));
          }, 5000);
          const updated = prev.filter((t) => t.userId !== data.userId);
          return [...updated, { userId: data.userId, timeout }];
        } else {
          return prev.filter((t) => t.userId !== data.userId);
        }
      });
    });

    return () => {
      wsService.emit('leave_conversation', { conversationId });
      unsubMsg();
      unsubTyping();
    };
  }, [conversationId, user?.id, user?.email]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Polling fallback (every 30s)
  useEffect(() => {
    if (!conversationId) return;
    const poll = async () => {
      try {
        const msgs = await messagesApi.getMessages(conversationId);
        setMessages(msgs.map(normalizeMessage));
      } catch {}
    };
    const timer = setInterval(poll, 30_000);
    return () => clearInterval(timer);
  }, [conversationId]);

  // Emit typing indicator with debounce
  const handleInputChange = useCallback((value: string) => {
    setNewMessage(value);
    if (!conversationId) return;
    wsService.emit('typing', { conversationId, isTyping: true });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      wsService.emit('typing', { conversationId, isTyping: false });
    }, 2000);
  }, [conversationId]);

 const uploadFiles_REPLACEMENT = async (files: File[]): Promise<any[]> => {
  const attachments: any[] = [];
  const total = files.length;
 
  for (let idx = 0; idx < files.length; idx++) {
    const file = files[idx];
    setUploadProgress(Math.round(((idx) / total) * 100));
 
    const fileExt = file.name.split('.').pop() || 'bin';
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
    const filePath = `deal-room-files/${contactRequest.dealId}/${conversationId}/${fileName}`;
 
    const storageRef = ref(storage, filePath);
    await uploadBytes(storageRef, file);
 
    const downloadUrl = await getDownloadURL(storageRef);
 
    attachments.push({
      name: file.name,
      url: downloadUrl,
      type: file.type,
      size: file.size,
    });
 
    setUploadProgress(Math.round(((idx + 1) / total) * 100));
  }
 
  return attachments;
};

  const validateAndAddFiles = (files: File[]) => {
    const validFiles: File[] = [];

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: isFr ? 'Fichier trop volumineux' : 'File too large',
          description: isFr ? `${file.name} dépasse la limite de 10 MB` : `${file.name} exceeds the 10 MB limit`,
          variant: 'destructive',
        });
        continue;
      }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        toast({
          title: isFr ? 'Type de fichier non supporté' : 'Unsupported file type',
          description: isFr ? `${file.name} n'est pas un type de fichier supporté` : `${file.name} is not a supported file type`,
          variant: 'destructive',
        });
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setPendingFiles((prev) => {
        const total = prev.length + validFiles.length;
        if (total > MAX_FILES) {
          const allowed = MAX_FILES - prev.length;
          toast({
            title: isFr ? 'Limite atteinte' : 'Limit reached',
            description: isFr
              ? `Maximum ${MAX_FILES} fichiers simultanés. ${validFiles.length - allowed} fichier(s) ignoré(s).`
              : `Maximum ${MAX_FILES} files at once. ${validFiles.length - allowed} file(s) skipped.`,
            variant: 'destructive',
          });
          const acceptedFiles = validFiles.slice(0, allowed);
          generateImagePreviews(acceptedFiles, prev.length);
          return [...prev, ...acceptedFiles];
        }
        generateImagePreviews(validFiles, prev.length);
        return [...prev, ...validFiles];
      });
    }
  };

  // Generate thumbnails for image files
  const generateImagePreviews = (files: File[], startIndex: number) => {
    files.forEach((file, i) => {
      if (isImageType(file.type)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreviews((prev) => ({ ...prev, [startIndex + i]: e.target?.result as string }));
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    validateAndAddFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set false if leaving the drop zone (not entering a child)
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      validateAndAddFiles(files);
    }
  }, [isFr, toast]);

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const handleSend = useCallback(async () => {
    if ((!newMessage.trim() && pendingFiles.length === 0) || !conversationId) return;

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    wsService.emit('typing', { conversationId, isTyping: false });

    setIsSending(true);
    setIsUploading(pendingFiles.length > 0);
    setUploadProgress(0);

    try {
      let attachments: Attachment[] = [];

      // Upload files first
      if (pendingFiles.length > 0) {
        attachments = await uploadFiles_REPLACEMENT(pendingFiles);
      }

      // Build message content
      const messageContent = newMessage.trim() || (
        attachments.length === 1
          ? (isFr ? `📎 Fichier partagé: ${attachments[0].name}` : `📎 File shared: ${attachments[0].name}`)
          : (isFr ? `📎 ${attachments.length} fichiers partagés` : `📎 ${attachments.length} files shared`)
      );

      const msg = await messagesApi.sendMessage(conversationId, messageContent);
      setMessages((prev) => {
        const normalized = normalizeMessage({
          ...msg,
          senderId: msg.senderId || (msg as any).sender_id || user?.id || '',
          senderName: msg.senderName || (msg as any).sender_name || user?.displayName || '',
          attachments,
        });
        if (prev.some((m) => m.id === normalized.id)) return prev;
        return [...prev, normalized];
      });

      setNewMessage('');
      setPendingFiles([]);
      setImagePreviews({});
      setUploadProgress(0);
    } catch {
      toast({
        title: isFr ? 'Erreur' : 'Error',
        description: isFr ? "Impossible d'envoyer le message" : 'Failed to send message',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
      setIsUploading(false);
    }
  }, [newMessage, conversationId, user, isFr, toast, pendingFiles]);

  // Collect all images from all messages for lightbox navigation
  const allImages = messages.flatMap((msg) =>
    (msg.attachments || []).filter((a) => isImageType(a.type)).map((a) => ({ url: a.url, name: a.name }))
  );
  const currentLightboxIndex = lightboxImage ? allImages.findIndex((img) => img.url === lightboxImage.url) : -1;

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!lightboxImage) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLightboxImage(null);
      } else if (e.key === 'ArrowLeft' && currentLightboxIndex > 0) {
        setLightboxImage(allImages[currentLightboxIndex - 1]);
      } else if (e.key === 'ArrowRight' && currentLightboxIndex < allImages.length - 1) {
        setLightboxImage(allImages[currentLightboxIndex + 1]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, currentLightboxIndex, allImages]);

  if (contactRequest.status !== 'accepted') {
    return null;
  }

  const handleDeleteMessage = async (messageId: string) => {
    setIsDeleting(true);
    try {
      await messagesApi.deleteMessage(messageId);
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      toast({
        title: isFr ? 'Message supprimé' : 'Message deleted',
      });
    } catch {
      toast({
        title: isFr ? 'Erreur' : 'Error',
        description: isFr ? 'Impossible de supprimer le message' : 'Failed to delete message',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return '?';
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const renderAttachments = (attachments?: Attachment[]) => {
    if (!attachments || attachments.length === 0) return null;

    return (
      <div className="mt-2 space-y-1.5">
        {attachments.map((att, i) => (
          <div key={i}>
            {isImageType(att.type) ? (
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxImage({ url: att.url, name: att.name }); }}
                className="block"
              >
                <img
                  src={att.url}
                  alt={att.name}
                  className="max-w-[200px] max-h-[150px] rounded-md border border-border object-cover cursor-pointer hover:opacity-90 transition-opacity"
                />
              </button>
            ) : (
              <a
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-2 rounded-md bg-muted/50 border border-border hover:bg-muted transition-colors text-xs"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate flex-1">{att.name}</span>
                <span className="text-muted-foreground shrink-0">{formatFileSize(att.size)}</span>
                <Download className="h-3 w-3 shrink-0 text-muted-foreground" />
              </a>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
    <Card className="border-green-200 bg-green-50/30">
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-green-600" />
            {isFr ? 'Communication - Deal Room' : 'Deal Room Communication'}
            {messages.length > 0 && (
              <Badge variant="secondary" className="text-xs">{messages.length}</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
              {wsService.isConnected ? (isFr ? 'En direct' : 'Live') : (isFr ? 'Hors ligne' : 'Offline')}
            </Badge>
            {contactRequest.meetingLink && (
              <a href={contactRequest.meetingLink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="outline" className="h-7 gap-1 text-xs">
                  <Video className="h-3 w-3" />
                  {isFr ? 'Réunion' : 'Meeting'}
                </Button>
              </a>
            )}
            <span className="text-xs text-muted-foreground">{isExpanded ? '▲' : '▼'}</span>
          </div>
        </CardTitle>
      </CardHeader>

      {isExpanded && (
        <CardContent
          ref={dropZoneRef}
          className={`space-y-3 pt-0 relative transition-colors ${isDragOver ? 'bg-primary/5' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Drag overlay */}
          {isDragOver && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/5 pointer-events-none">
              <div className="text-center">
                <Paperclip className="h-8 w-8 mx-auto text-primary mb-2" />
                <p className="text-sm font-medium text-primary">
                  {isFr ? 'Déposez vos fichiers ici' : 'Drop your files here'}
                </p>
              </div>
            </div>
          )}
          {contactRequest.message && (
            <div className="bg-white rounded-lg p-3 border border-green-100">
              <p className="text-xs text-muted-foreground mb-1">
                {isFr ? 'Message initial de contact' : 'Initial contact message'}
              </p>
              <p className="text-sm italic text-muted-foreground">"{contactRequest.message}"</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <ScrollArea className="h-[300px] pr-3" ref={scrollRef as any}>
                <div className="space-y-3">
                  {messages.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-8">
                      {isFr ? 'Aucun message encore. Commencez la conversation!' : 'No messages yet. Start the conversation!'}
                    </p>
                  ) : (
                    messages.map((msg) => {
                      const isMe = msg.senderId === user?.id || msg.senderId === user?.email;
                      return (
                        <div key={msg.id} className={`flex gap-2 group/msg ${isMe ? 'flex-row-reverse' : ''}`}>
                          <Avatar className="h-7 w-7 shrink-0">
                            <AvatarFallback className={`text-xs ${isMe ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                              {getInitials(msg.senderName)}
                            </AvatarFallback>
                          </Avatar>
                          <div className={`max-w-[75%] ${isMe ? 'text-right' : ''}`}>
                            <div className={`rounded-lg px-3 py-2 text-sm relative ${
                              isMe ? 'bg-primary text-primary-foreground' : 'bg-white border border-border'
                            }`}>
                              {msg.content}
                              {renderAttachments(msg.attachments)}
                              {isMe && (
                                <button
                                  onClick={() => setDeleteConfirm(msg.id)}
                                  className="absolute -top-2 -right-2 opacity-0 group-hover/msg:opacity-100 transition-opacity bg-destructive text-destructive-foreground rounded-full p-1 shadow-sm hover:bg-destructive/90"
                                  title={isFr ? 'Supprimer' : 'Delete'}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-0.5 px-1">
                              {msg.senderName && <span className="font-medium">{msg.senderName} · </span>}
                              {new Date(msg.createdAt).toLocaleTimeString(isFr ? 'fr-CA' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}

                  {/* Typing indicator */}
                  {typingUsers.length > 0 && (
                    <div className="flex items-center gap-2 pl-1">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-xs text-muted-foreground italic">
                        {isFr ? 'En train d\'écrire...' : 'Typing...'}
                      </span>
                    </div>
                  )}
                </div>
              </ScrollArea>

              {/* Pending file previews */}
              {pendingFiles.length > 0 && (
                <div className="space-y-2 px-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {pendingFiles.length}/{MAX_FILES} {isFr ? 'fichiers' : 'files'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {pendingFiles.map((file, i) => (
                      <div key={i} className="relative group">
                        {isImageType(file.type) && imagePreviews[i] ? (
                          <div className="relative w-16 h-16 rounded-md overflow-hidden border border-border">
                            <img
                              src={imagePreviews[i]}
                              alt={file.name}
                              className="w-full h-full object-cover"
                            />
                            <button
                              onClick={() => removePendingFile(i)}
                              className="absolute top-0 right-0 bg-destructive text-destructive-foreground rounded-bl-md p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5">
                              <span className="text-[9px] text-white truncate block">{formatFileSize(file.size)}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 bg-muted rounded-md px-2 py-1 text-xs border">
                            <FileText className="h-3 w-3 text-muted-foreground" />
                            <span className="truncate max-w-[120px]">{file.name}</span>
                            <span className="text-muted-foreground">{formatFileSize(file.size)}</span>
                            <button
                              onClick={() => removePendingFile(i)}
                              className="ml-1 text-muted-foreground hover:text-destructive transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Message input */}
              <div className="flex gap-2 pt-2 border-t items-end">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ALLOWED_FILE_TYPES.join(',')}
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="shrink-0 h-9 w-9"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSending}
                  title={isFr ? 'Joindre un fichier' : 'Attach a file'}
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
                <Input
                  value={newMessage}
                  onChange={(e) => handleInputChange(e.target.value)}
                  placeholder={isFr ? 'Écrivez votre message...' : 'Type your message...'}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  disabled={isSending}
                  className="flex-1"
                />
                <Button
                  size="icon"
                  onClick={handleSend}
                  disabled={isSending || (!newMessage.trim() && pendingFiles.length === 0)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              {isUploading && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{isFr ? 'Téléversement...' : 'Uploading...'}</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      )}
    </Card>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxImage(null)}
        >
          {/* Close button */}
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
            onClick={() => setLightboxImage(null)}
          >
            <X className="h-8 w-8" />
          </button>

          {/* Nav: Previous */}
          {allImages.length > 1 && currentLightboxIndex > 0 && (
            <button
              className="absolute left-4 text-white/80 hover:text-white z-10"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxImage(allImages[currentLightboxIndex - 1]);
              }}
            >
              <ChevronLeft className="h-10 w-10" />
            </button>
          )}

          {/* Image */}
          <img
            src={lightboxImage.url}
            alt={lightboxImage.name}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Nav: Next */}
          {allImages.length > 1 && currentLightboxIndex < allImages.length - 1 && (
            <button
              className="absolute right-4 text-white/80 hover:text-white z-10"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxImage(allImages[currentLightboxIndex + 1]);
              }}
            >
              <ChevronRight className="h-10 w-10" />
            </button>
          )}

          {/* Caption */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/50 px-4 py-2 rounded-full">
            {lightboxImage.name}
            {allImages.length > 1 && ` (${currentLightboxIndex + 1}/${allImages.length})`}
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isFr ? 'Supprimer ce message ?' : 'Delete this message?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isFr
                ? 'Cette action est irréversible. Le message sera définitivement supprimé.'
                : 'This action cannot be undone. The message will be permanently deleted.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {isFr ? 'Annuler' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDeleteMessage(deleteConfirm)}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              {isFr ? 'Supprimer' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
